import { Link } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Map, ShieldCheck, ClipboardCheck, ShieldAlert, WifiOff, QrCode } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../lib/roles";

const Section = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-2">
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center shrink-0">
        <Icon className="w-4.5 h-4.5 text-cyan-400" />
      </div>
      <h3 className="text-sm font-black uppercase tracking-wide text-slate-100">{title}</h3>
    </div>
    <div className="text-sm text-slate-400 leading-relaxed space-y-1.5">{children}</div>
  </div>
);

export default function Help() {
  const { user, openTour } = useAuth();

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-slate-100">Aide &amp; Tutoriel</h1>
          <p className="text-sm text-slate-500 mt-1">Guide d'utilisation de l'application d'inspection des fronts.</p>
        </div>
        <div className="flex gap-2">
          {user && (
            <button data-testid="help-replay-tour-button" onClick={openTour} className="text-xs font-mono uppercase tracking-widest text-cyan-400 border border-cyan-800 rounded-md px-3 py-2 hover:bg-cyan-500/10">
              Revoir le guide interactif
            </button>
          )}
          <Link data-testid="help-back-link" to="/" className="text-xs font-mono uppercase tracking-widest text-slate-400 border border-slate-700 rounded-md px-3 py-2 hover:bg-slate-800/60 flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour
          </Link>
        </div>
      </div>

      <Section icon={LayoutGrid} title="Page d'accueil" testId="help-section-home">
        <p>Affiche une synthèse KPI (fronts autorisés/avec réserves/suspendus, anomalies critiques, actions en retard) et la liste des alertes prioritaires à traiter.</p>
      </Section>

      <Section icon={Map} title="Vue carrière" testId="help-section-vue-carriere">
        <p>Liste tous les fronts avec leur statut actuel. Triez par priorité (les plus critiques en premier) ou par ordre géographique.</p>
      </Section>

      <Section icon={ShieldCheck} title="Fiche d'un front" testId="help-section-front-detail">
        <p>6 onglets : <b>Aperçu</b> (statut, dernières infos), <b>Inspections</b> (historique complet), <b>Anomalies</b>, <b>Actions</b> correctives, <b>Photos</b>, <b>Historique</b> des changements de statut. Un graphique affiche l'évolution du score de conformité sur 30 jours.</p>
      </Section>

      <Section icon={ClipboardCheck} title="Réaliser une inspection (Inspecteur)" testId="help-section-inspection">
        <p>Depuis la fiche d'un front, cliquez sur "Nouvelle inspection". Répondez à chaque question du questionnaire, ajoutez une photo et un commentaire si nécessaire.</p>
        <p><b>Règle de sécurité essentielle :</b> une anomalie critique suspend automatiquement le front. Cette suspension ne peut jamais être levée automatiquement par une inspection suivante — seul un Responsable ou un Administrateur peut valider une remise en exploitation.</p>
      </Section>

      <Section icon={ShieldAlert} title="Remise en exploitation (Responsable / Administrateur)" testId="help-section-reactivation">
        <p>Un front suspendu affiche un bouton "Demander la remise en exploitation" réservé au Responsable et à l'Administrateur. Le workflow demande une confirmation explicite et un commentaire de validation.</p>
      </Section>

      <Section icon={WifiOff} title="Mode hors ligne" testId="help-section-offline">
        <p>Sur le terrain sans réseau, l'application continue de fonctionner avec les dernières données déjà consultées. Un bandeau "Hors ligne" apparaît en haut de l'écran. Toute inspection réalisée hors ligne est automatiquement envoyée dès que la connexion revient — aucune action manuelle nécessaire.</p>
      </Section>

      {hasRole(user, "RESPONSABLE", "ADMINISTRATEUR") && (
        <Section icon={QrCode} title="Administration" testId="help-section-admin">
          <p>Gérez les utilisateurs (créer/modifier/supprimer, rôles et site d'affectation), les fronts, les questions du checklist, les QR codes par front et consultez le journal d'audit.</p>
          {hasRole(user, "ADMINISTRATEUR") && <p>En tant qu'Administrateur, un sélecteur en haut de l'écran permet de filtrer toute l'application par site (usine) ou de voir tous les sites à la fois.</p>}
        </Section>
      )}
    </div>
  );
}
